import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { QuestionStatisStudentComponent } from '@app/task/task-analysis/components/question-statis-student/question-statis-student.component';
import { TaskAnalysisService } from '@app/task/task-analysis/task-analysis.service';
import { QuestionStatis, StudentScore, TaskAnalysisGraph, WrongAnswerData } from '@shared/model/analysis.models';
import { PaperDetail, Question, QuestionType } from '@shared/model/itembank.models';
import { TaskAnalysis, TaskQuestionCorrectPercent, TaskTypeEnum } from '@shared/model/task.models';
import { QuestionExplainOverlayService } from '@shared/question-explain/question-explain-overlay.service';
import {
  fmtQuestionIncorrectStatis,
  SelectOption,
} from '@shared/question/components/quick-uptake/components/quick-uptake-select/quick-uptake-select.component';
import { QuickUptakeComponent } from '@shared/question/components/quick-uptake/quick-uptake.component';
import { AiVariantComponent } from '@shared/question/components/ai-variant/ai-variant.component';
import { QuestionRootService } from '@shared/question/question-root.service';
import { QuestionComponent } from '@shared/question/question.component';
import { AnalysisService } from '@shared/service/analysis.service';
import { ItembankService } from '@shared/service/itembank.service';
import { SharedService } from '@shared/service/shared.service';
import { getEnvaluateColor, getErrorEnvaluateColor } from '@shared/utils/utils';
import dayjs from 'dayjs';
import { isNil } from 'ng-zorro-antd/core/util';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import { cloneDeep, uniq, uniqBy } from 'es-toolkit';
import { AiReportTypeEnum, RxjsSubscription, TabsItem } from '@common';
import { MarkEnter, MarkMode, MarkOperateType, MarkParamsWithTask } from '@fuulea/mark-paper';
import { getTaskMarkQuestion, toMark } from '@app/mark/utils';
import { AlgoJobStatusEnum } from '@shared/model/ai.models';
import { MarkUtilsService } from '@app/mark/mark-utils.api.service';
import ShareReasonService from '@shared/service/share-reason.service';
import {
  MessageApiService,
  Message,
  MessageThreadOpenedEvent,
  MessageThreadQuery,
  TaskStatisItem,
  MessageType,
} from '@app/message/message.api.service';
import { ReplyTarget, ReplySendPayload } from '@app/message/components/reply/reply-composer.component';

export interface QuestionStatis4SelectOption extends SelectOption {
  width?: number;
  backgroundColor?: string;
}

enum ResultTabsEnum {
  Detail = 'detail',
  Node = 'node',
  Feedback = 'feedback',
}

@Component({
  selector: 'fl-task-analysis',
  templateUrl: './task-analysis.component.html',
  styleUrls: ['./task-analysis.component.scss'],
  providers: [QuestionExplainOverlayService, MarkUtilsService],
  standalone: false,
})
export class TaskAnalysisComponent implements OnInit, OnDestroy {
  readonly questionTpl = viewChild<QuestionComponent>('questionTpl');
  protected aiAvatar = 'https://s-chengdu.100tifen.com/miniprogram/static/image/task/avatar.png';
  private taskSubscription = new RxjsSubscription();

  // 当前的Task
  task: TaskAnalysis = null;

  // 当前的题目序号
  currentQuestionNo: string = '';

  /** 当前展示的任务 id：识别跨任务切换（组件复用时不重跑 ngOnInit，task 流重发时据此复位选题） */
  private lastLoadedTaskId?: number;
  // 当前的题组题目题号
  stemQuestionNo: string = '';
  // 当前的题目
  question: Question;
  //  当前选择的小题
  childQuestion: Question;

  questionStatis: QuestionStatis = null;
  questionStatisAnswers: QuestionStatis4SelectOption[] = [];

  questionTabIndex: ResultTabsEnum = ResultTabsEnum.Detail;

  taskQuestionGraph: TaskAnalysisGraph[] = [];

  // 控制知识点图谱展开/收起状态
  isGraphExpanded: boolean = false;

  getEnvaluateColor = getEnvaluateColor;
  TaskTypeEnum = TaskTypeEnum;

  // 英语答案聚合数据
  wrongAnswers: Array<WrongAnswerData> = [];

  // 标记聚合标签
  flagTags: Array<{ flag: number; label: string; count: number; students: StudentScore[] }> = [];

  QuestionType = QuestionType;

  paper: PaperDetail;

  aiReportTypeEnum = AiReportTypeEnum;

  resultTabs: TabsItem[] = [];

  ResultTabsEnum = ResultTabsEnum;

  /** 全任务消息子话题统计（thread 列表，paperQuestionId → thread_id 映射源） */
  taskMessageStatis: TaskStatisItem[] = [];

  /** statis 对应的任务 id（防路由复用导致用旧任务映射错线程） */
  private statisTaskId?: number;

  /** 最近一次已应用的 questionId（防同任务跳转时 queryParams 订阅与 task 订阅重复定位） */
  private lastAppliedQuestionId?: number;

  /** 当前题映射到的消息线程（喂给 fl-message-list） */
  messageThreadQuery?: MessageThreadQuery;

  /** 当前题的 statis 项（喂给顶部 fl-task-message-static，取题号/得分率） */
  currentStatisItem?: TaskStatisItem;

  /** 当前题的消息列表（喂给顶部 static 做 tag 聚合，由 message-list messagesChange 缓冲） */
  currentQuestionMessages: Message[] = [];

  /** 回复成功后递增，触发 message-list 重拉 */
  messageRefreshTick = 0;

  /** 回复器目标：`{}` 表示回复全班（常驻，无具体对象）；有 msgId 回复个人 */
  replyTarget: ReplyTarget = {};

  /** 清空信号：发送/关闭/切题时递增，驱动常驻回复器清空输入 */
  clearTick = 0;

  /** 「同步全班」勾选状态 */
  toAll = false;

  // since 26.5.19 选择题拍照的数量
  choiceDraftsCount = 0;

  get disabledCreateTargetClass() {
    return !this.questionStatisAnswers.some(i => i.checked);
  }

  get classroomId() {
    if (typeof this.taskAnalysisSrv.currentTaskId !== 'number') return null;
    return this.taskAnalysisSrv.allTaskPackDetails.find(x => x.taskId === this.taskAnalysisSrv.currentTaskId)
      .classroomId;
  }

  get currentQuestionStatis() {
    if (!this.question) return null;
    return this.task.questionStatis.find(x => x.questionId === this.question.id);
  }

  /**
   * 是否展示AI变式题。目前只有高中物理展示
   */
  get isShowAiQuestion() {
    return this.sharedSrv.user.subject_id === 3;
  }

  public taskAnalysisSrv: TaskAnalysisService = inject(TaskAnalysisService);
  private analysisSrv: AnalysisService = inject(AnalysisService);
  private drawerSrv: NzDrawerService = inject(NzDrawerService);
  private title: Title = inject(Title);
  public sharedSrv: SharedService = inject(SharedService);
  private shareReasonService = inject(ShareReasonService);
  private router: Router = inject(Router);
  private itembankSrv: ItembankService = inject(ItembankService);
  private questionExplainSrv: QuestionExplainOverlayService = inject(QuestionExplainOverlayService);
  private questionRootSrv: QuestionRootService = inject(QuestionRootService);
  private route = inject(ActivatedRoute);
  private markUtilsSrv = inject(MarkUtilsService);
  private messageApiSrv = inject(MessageApiService);
  private messageSrv = inject(NzMessageService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    // 滚动到顶部
    window.scroll(0, 0);
    // 初始化右下角 tabs
    this.initTabs();
    this.taskSubscription.add(
      this.taskAnalysisSrv.task.subscribe(async task => {
        if (!task) return;
        this.title.setTitle(`任务分析 - ${task.title} - ${this.sharedSrv.site.siteTitle}`);
        this.task = {
          ...task,
          // 如果是模块任务,则需要重新格式化序号
          questionStatis: [TaskTypeEnum.Exercise, TaskTypeEnum.Card, TaskTypeEnum.WorkBook].includes(task.type)
            ? task.questionStatis
            : fmtPaperNo(task.questionStatis),
        };
        // 跨任务切换（组件复用，如消息抽屉跳另一任务）：复位选题，避免旧题号在新任务里查不到而报错；questionId 参数随后覆盖到目标题
        const taskChanged = this.lastLoadedTaskId !== task.id;
        this.lastLoadedTaskId = task.id;
        if (taskChanged) {
          this.currentQuestionNo = '';
          this.questionTabIndex = ResultTabsEnum.Detail;
        }
        // 个性化错题等任务没有题目统计：无题目链可加载，直接标记后返回
        // （若不判空会读取空数组抛错中断订阅回调，OnPush 下 this.task 赋值后的头部/未完成名单区将不再渲染）
        if (!this.task.questionStatis?.length) {
          this.cdr.markForCheck();
          return;
        }
        let paperQuestionId = this.task.questionStatis[0].paperQuestionId;
        let paperId = this.task.questionStatis[0].paperId;
        let questionStatis = this.task.questionStatis[0];
        if (this.currentQuestionNo === '') {
          this.currentQuestionNo = this.task.questionStatis[0].no;
        } else {
          const statis = this.task.questionStatis.find(x => x.no === this.currentQuestionNo);
          paperQuestionId = statis.paperQuestionId;
          paperId = statis.paperId;
          questionStatis = statis;
        }
        /**
         * 从反馈过来。如果有 questionId,就要选中这个 question。
         */
        // 首次/跨任务进入带 questionId：定位到对应题（命中则跳过默认第一题加载，避免重复请求）
        const queryQuestionId = this.route.snapshot.queryParamMap.get('questionId');
        if (queryQuestionId != null) {
          const qid = parseInt(queryQuestionId, 10);
          if (await this.applyQuestionIdNavigation(qid)) {
            this.lastAppliedQuestionId = qid;
            return;
          }
        }
        await Promise.all([
          this.getQuestionFromPaper(paperQuestionId, paperId),
          this.getQuestionIncorrect(questionStatis),
        ]);
        this.getQuestionDiscuss(questionStatis);
        this.getQuestionGraph();
        // OnPush：任务数据（含题目/统计）经 await 拉取后写入，非模板事件需主动标记
        this.cdr.markForCheck();
      })
    );
    // 同任务跳转（路由 :id 不变、组件复用、task 流不重发）时，queryParams 的 questionId 变化同样定位题目
    this.taskSubscription.add(
      this.route.queryParams.subscribe(params => {
        const qid = params['questionId'];
        if (qid == null) return;
        const questionId = parseInt(qid, 10);
        if (Number.isNaN(questionId) || !this.task || questionId === this.lastAppliedQuestionId) return;
        this.lastAppliedQuestionId = questionId;
        this.applyQuestionIdNavigation(questionId);
      })
    );
    // 题目收藏变化
    this.taskAnalysisSrv.taskSubscription.subscriptions = this.questionRootSrv.beFavorite$.subscribe(
      ({ questionId, beFavorited }) => {
        const q = this.paper.questions.find(x => x.id === questionId);
        if (q) {
          q['beFavorited'] = beFavorited;
          // OnPush：收藏态原地写入题目对象，非模板事件需主动标记
          this.cdr.markForCheck();
        }
      }
    );
    // 视频变化
    this.taskAnalysisSrv.taskSubscription.subscriptions = this.questionRootSrv.video$.subscribe(
      ({ questionId, video, hasVideo }) => {
        let q = this.paper.questions.find(x => x.id === questionId);
        // 如果是小题
        if (!q) {
          q = this.paper.questions.flatMap(i => i.children || []).find(x => x.id === questionId);
        }
        if (q) {
          q['video'] = video;
          q['hasVideo'] = hasVideo;
          // OnPush：视频态原地写入题目对象，非模板事件需主动标记
          this.cdr.markForCheck();
        }
      }
    );
    // 知识点变化
    this.taskAnalysisSrv.taskSubscription.subscriptions = this.questionRootSrv.nodes$.subscribe(
      ({ questionId, nodes }) => {
        let q = this.paper.questions.find(x => x.id === questionId);
        // 如果是小题
        if (!q) {
          q = this.paper.questions.flatMap(i => i.children || []).find(x => x.id === questionId);
        }
        if (q) {
          q['nodes'] = nodes;
          // OnPush：知识点原地写入题目对象，非模板事件需主动标记
          this.cdr.markForCheck();
        }
      }
    );
  }

  ngOnDestroy() {
    this.taskSubscription.unsubscribe();
  }

  initTabs() {
    this.resultTabs = [
      {
        name: '作答详情',
        key: ResultTabsEnum.Detail,
      },
      {
        name: '知识分析',
        key: ResultTabsEnum.Node,
        disabled:
          this.task?.type === TaskTypeEnum.Vocabulary ||
          (this.question?.model === QuestionType.Stem && !this.childQuestion),
      },
      {
        name: '题目反馈',
        key: ResultTabsEnum.Feedback,
      },
    ];
  }

  tabsChange(e: ResultTabsEnum) {
    const element = document.getElementById(`${e}`);
    if (element) {
      const questionDetailContent = document.querySelector('.question-detail-content');
      if (questionDetailContent) {
        questionDetailContent.scrollTo({
          top: element.offsetTop - 50,
          behavior: 'smooth',
        });
      }
    }
  }

  /**
   * 通过试卷拿题目详情
   */
  async getQuestionFromPaper(paperQuestionId: number, paperId: number) {
    // 如果是当前题目,不需要重新赋值
    if (
      (this.question && this.question['paperQuestionId'] === paperQuestionId && !this.childQuestion) ||
      (this.childQuestion && this.childQuestion['paperQuestionId'] === paperQuestionId)
    ) {
      return;
    }
    this.question = null;
    this.childQuestion = null;

    if (this.paper?.id !== paperId) {
      this.paper = await this.itembankSrv.getPaper(paperId, this.task.id);
    }
    this.paper.questions.forEach(q => {
      const _q = cloneDeep(q);
      if (q.paperQuestionId === paperQuestionId) {
        this.question = _q;
        this.stemQuestionNo = this.task.questionStatis.find(question => question.questionId === this.question.id)?.no;
      }
      if (q.paperQuestionId !== paperQuestionId && q.model === QuestionType.Stem) {
        if (q.children.findIndex(i => i.paperQuestionId === paperQuestionId) !== -1) {
          this.question = _q;
          this.stemQuestionNo = this.task.questionStatis.find(question => question.questionId === this.question.id)?.no;
          this.childQuestion = _q.children.find(subQ => subQ.paperQuestionId === paperQuestionId);
          this.question['children'] = [];
        }
      }
    });
  }

  /**
   * 获取题目的错题统计
   * @param {TaskQuestionCorrectPercent} question
   */
  async getQuestionIncorrect(question: TaskQuestionCorrectPercent) {
    this.questionStatis = await this.analysisSrv.getClassPaperQuestionIncorrect({
      paperId: question.paperId,
      paperQuestionId: question.paperQuestionId,
      classroomId: this.taskAnalysisSrv.currentClassroomId ?? this.classroomId,
      chapterExercise: false,
      taskId: this.task.id,
      includeRelatedTasks:
        typeof this.taskAnalysisSrv.currentTaskId === 'number'
          ? null
          : (this.taskAnalysisSrv.currentTaskId as 'my' | 'all'),
    });
    this.fmtStatisAnswers();
    this.computeFlagTags();
    // since 26.5.19 选择题拍了照的照片数量
    this.choiceDraftsCount = this.questionStatis.answers.filter(e => !!(e.photos ?? []).length).length;
  }

  /**
   * 拉取任务消息子话题统计（thread 列表），提供 paperQuestionId → thread_id 映射
   * 幂等：已加载则复用，避免每次切题重复请求
   */
  private async loadTaskMessageStatis() {
    if (!this.task?.id) return;
    if (this.statisTaskId === this.task.id) return;
    this.statisTaskId = this.task.id;
    try {
      this.taskMessageStatis = await this.messageApiSrv.getTaskStatis(this.task.id);
    } catch {
      this.taskMessageStatis = [];
    }
  }

  /**
   * 当前题同步消息线程：按 question.paperQuestionId 在 statis 中 find，组装 thread 查询并更新反馈角标
   */
  private syncMessageThread(question: TaskQuestionCorrectPercent) {
    // 切题/换任务后清空残留回复器，避免把上一题的回复发到当前题线程
    this.onCancelReply();
    this.currentQuestionMessages = [];
    const item = this.taskMessageStatis.find(s => s.paperQuestionId === question.paperQuestionId);
    this.currentStatisItem = item;
    this.messageThreadQuery = item
      ? {
          threadId: item.id,
          taskId: this.task.id,
          paperQuestionId: item.paperQuestionId,
          moduleId: item.moduleId,
        }
      : undefined;
    // 反馈角标：未读新消息数；无对应 thread 时归零
    this.resultTabs[this.resultTabs.length - 1].badge = item?.unReadCount ?? 0;
  }

  /** 消息流变化滚动缓冲当前题消息（供顶部 static 聚合标签），无 thread 时清空 */
  onQuestionMessagesChange(messages: Message[]) {
    this.currentQuestionMessages = messages ?? [];
  }

  /** 当前题目 thread 首屏获取成功后，清除该题未读和反馈 tab 角标 */
  onQuestionThreadOpened(event: MessageThreadOpenedEvent) {
    const item = this.taskMessageStatis.find(statis => statis.id === event.query.threadId);
    const clearedCount = Math.max(0, item?.unReadCount ?? 0);
    if (!item || clearedCount === 0) return;

    this.taskMessageStatis = this.taskMessageStatis.map(statis =>
      statis.id === event.query.threadId ? { ...statis, unReadCount: 0 } : statis
    );
    this.currentStatisItem = this.taskMessageStatis.find(statis => statis.id === event.query.threadId);
    this.resultTabs[this.resultTabs.length - 1].badge = 0;
    this.messageApiSrv.consumeMessageUnread(clearedCount);
  }

  /** 点学生消息头像 → 唤起回复器，预填引用目标；回复器常驻反馈区最底部，需把滚动容器推到底才能露出 */
  onMessageAvatarClick(message: Message) {
    this.replyTarget = {
      msgId: message.id,
      firstName: message.firstName,
      content: message.content,
      messageType: message.messageType,
      avatar: message.avatar,
    };
    this.toAll = false;
    // 回复器由单行态变复合态（多出引用条与工具栏）要等变更检测生效，隔一帧再滚到底
    setTimeout(() => {
      const el = document.querySelector('.question-detail-content');
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, 0);
  }

  /** 关闭回复器：常驻模式保留回复器渲染，退回全班态（replyTarget 置空对象 + clearTick 清空输入，与消息列表一致） */
  onCancelReply() {
    this.replyTarget = {};
    this.toAll = false;
    this.clearTick += 1;
  }

  /** 发送回复：有 msgId 走 replyMessage（toAll 控制是否同步全班），无则对当前题 createMessage */
  async onSendReply(payload: ReplySendPayload) {
    const target = this.replyTarget;
    const query = this.messageThreadQuery;
    // 回复具体对象只需 msgId；回复全班需按当前题定位（带线程上下文）。不判 !target：全班态 replyTarget 为空对象也放行
    if (!target?.msgId && !query) return;
    if (!payload.content && payload.mediaUrls.length === 0) return;
    try {
      if (target.msgId) {
        await this.messageApiSrv.replyMessage(target.msgId, {
          content: payload.content,
          toAll: this.toAll,
          mediaUrls: payload.mediaUrls,
        });
      } else {
        await this.messageApiSrv.createMessage({
          taskId: this.task.id,
          paperQuestionId: query.paperQuestionId,
          moduleId: query.moduleId ?? null,
          content: payload.content,
          mediaUrls: payload.mediaUrls,
          type: MessageType.Default,
        });
      }
      this.messageRefreshTick += 1;
      this.onCancelReply();
      // OnPush：回复成功递增刷新 tick（@Input 传给消息列表），非模板事件需主动标记
      this.cdr.markForCheck();
    } catch (e: any) {
      this.messageSrv.error(e?.error?.detail ?? '发送失败，请重试');
    }
  }

  async getQuestionDiscuss(question: TaskQuestionCorrectPercent) {
    await this.loadTaskMessageStatis();
    this.syncMessageThread(question);
    if (this.questionTabIndex === ResultTabsEnum.Feedback) {
      setTimeout(() => {
        this.tabsChange(this.questionTabIndex);
      }, 0);
    }
    // OnPush：消息线程统计经 await 拉取后写入（含反馈角标），非模板事件需主动标记
    this.cdr.markForCheck();
  }

  /**
   * 按 questionId 定位到对应题目（命中则加载该题；未命中返回 false 保持默认第一题）
   *
   * 同任务跳转（路由 :id 不变、组件复用、task 流不重发）时 queryParams 的 questionId 同样变化，
   * 需与首次/跨任务进入共用同一套定位逻辑，故抽成公共方法。
   */
  private async applyQuestionIdNavigation(questionId: number): Promise<boolean> {
    const statis = this.task?.questionStatis?.find(x => x.questionId === questionId);
    if (!statis) return false;
    this.currentQuestionNo = statis.no;
    this.questionTabIndex = ResultTabsEnum.Feedback;
    await Promise.all([
      this.getQuestionFromPaper(statis.paperQuestionId, statis.paperId),
      this.getQuestionIncorrect(statis),
    ]);
    this.getQuestionDiscuss(statis);
    this.getQuestionGraph();
    // 从消息弹窗「查看原题」跳转定位到题后，页面滚到最底部，露出反馈消息区
    const scrollEl = document.scrollingElement ?? document.documentElement;
    scrollEl.scrollTop = scrollEl.scrollHeight;
    // OnPush：questionId 定位链的题目/统计经 await 拉取后写入，非模板事件需主动标记
    this.cdr.markForCheck();
    return true;
  }

  /**
   * 获取当前task的知识点统计
   */
  async getQuestionGraph() {
    const params = this.taskAnalysisSrv.getAggregateQueryParams();
    // 获取当前题目的所有知识点
    const questionNodeIds = uniq(
      []
        .concat(
          this.question.nodes.map(i => i.id),
          this.question.children?.map(i => i.nodes.map(i => i.id)),
          this.childQuestion?.nodes.map(i => i.id)
        )
        .filter(i => !isNil(i))
        .flat(2)
    );
    this.taskQuestionGraph = (await this.analysisSrv.getTaskGraph(params.scope, params.params)).filter(i =>
      questionNodeIds.includes(i.id)
    );
    // since 22/10/26  不过滤只与题目相关的知识点
    // this.taskQuestionGraph = (await this.analysisSrv.getTaskGraph(params.scope, params.params));
    // OnPush：知识点图经 await 拉取后写入，非模板事件需主动标记
    this.cdr.markForCheck();
  }

  /**
   * 格式化题目的错题统计
   */
  fmtStatisAnswers() {
    this.questionStatisAnswers = fmtQuestionIncorrectStatis(this.questionStatis).map(s => {
      return {
        ...s,
        bar: true,
        width: (s.count / this.task.studentCount) * 100,
        backgroundColor: getErrorEnvaluateColor(
          s.label,
          this.questionStatis.proper,
          [QuestionType.SingleChoice, QuestionType.MultiChoice].includes(this.questionStatis.model),
          s.maxScore
        ),
      };
    });
    this.questionStatisAnswers.unshift({
      label: '答错',
      value: '答错',
      students: this.questionStatis.answers.filter(i => i.score < Number(this.questionStatis.maxScore)),
      count: this.questionStatis.answers.length - this.questionStatis.rightCount,
      checked: true,
    });
    // 错误答案聚合
    this.wrongAnswers = this.questionStatis.annotateAnswers;
    // 如果超过8个就只显示前8个
    if (this.wrongAnswers && this.wrongAnswers.length > 8) {
      this.wrongAnswers = this.wrongAnswers.slice(0, 8);
    }
  }

  /**
   * 根据 flag 计算标记标签
   */
  private async computeFlagTags() {
    if (!this.questionStatis?.answers?.length) {
      this.flagTags = [];
      return;
    }

    // 获取原因映射
    const reasonList = await this.shareReasonService.getShareReasons();
    const reasonMap = new Map<number, string>(reasonList);
    reasonMap.set(1, '标记');

    // 按 flag 聚合，排除 0/null
    const flagGroups = new Map<number, StudentScore[]>();
    const sharedStudents: StudentScore[] = [];
    for (const answer of this.questionStatis.answers) {
      if (answer.reason && answer.reason !== '') {
        sharedStudents.push(answer);
      }
      if (!answer.flag) continue;
      if (!flagGroups.has(answer.flag)) {
        flagGroups.set(answer.flag, []);
      }
      flagGroups.get(answer.flag)!.push(answer);
    }

    this.flagTags = Array.from(flagGroups.entries())
      .map(([flag, students]) => ({
        flag,
        label: reasonMap.get(flag) ?? String(flag),
        count: students.length,
        students,
      }))
      .sort((a, b) => a.flag - b.flag);

    if (sharedStudents.length) {
      const sharedTag = {
        flag: 0,
        label: '已分享',
        count: sharedStudents.length,
        students: sharedStudents,
      };
      // 放在 flag=1（"标记"）的后面
      const insertIndex = this.flagTags.findIndex(t => t.flag === 1) + 1;
      this.flagTags.splice(insertIndex, 0, sharedTag);
    }
    // OnPush：标记标签经 await 拉取原因映射后写入，非模板事件需主动标记
    this.cdr.markForCheck();
  }

  /** 点击标记标签，展示对应学生列表 */
  viewFlagStudents(event: Event, flagTag: { flag: number; label: string; count: number; students: StudentScore[] }) {
    this.viewStudent(event, {
      students: flagTag.students,
      value: String(flagTag.flag),
      label: flagTag.label,
      count: flagTag.count,
    });
  }

  /**
   * 点击切换题目
   * @param {TaskQuestionCorrectPercent} question
   */
  async changeQuestion(question: TaskQuestionCorrectPercent) {
    this.currentQuestionNo = question.no;

    await this.getQuestionFromPaper(question.paperQuestionId, question.paperId);
    // await this.getQuestion(question.questionId);
    await this.getQuestionIncorrect(question);
    this.getQuestionDiscuss(question);

    this.questionTabChange();

    if (this.question?.model === QuestionType.Stem && !this.childQuestion)
      this.questionTabIndex = ResultTabsEnum.Detail;
    // OnPush：切题后题目/统计经 await 拉取后写入，非模板事件需主动标记
    this.cdr.markForCheck();
  }

  viewNotAnswerStudent(event, select: QuestionStatis4SelectOption) {
    event.stopPropagation();
    event.preventDefault();
    const { answers, model, statis } = this.questionStatis;
    const { label } = select;
    let students = [];
    if ([QuestionType.SingleChoice, QuestionType.MultiChoice].includes(model)) {
      const selectAnswers = label.split(':');
      students = answers.filter(i => {
        const answer = i.answer.split(':');
        return answer.every(i => !selectAnswers.includes(i));
      });
    } else {
      const ids = uniqBy(statis, item => item.group)
        .filter(i => i.group !== select.value)
        .map(i => i.detailIds)
        .flat();
      students = answers.filter(i => ids.includes(i.detailId));
    }
    const _select = { students: students, label: '', value: '', count: 0 };
    this.viewStudent(event, _select);
  }

  /**
   * 显示当前题目的答案分类的学生列表详情
   * @param {QuestionStatis4SelectOption} select
   */
  viewStudent(event, select?: QuestionStatis4SelectOption) {
    event.stopPropagation();
    event.preventDefault();
    const students = select ? select.students : this.questionStatis.answers;
    if (students.length <= 0) return;
    this.drawerSrv.create({
      nzWidth: 800,
      nzTitle: null,
      nzClosable: false,
      nzContent: QuestionStatisStudentComponent,
      nzBodyStyle: { padding: '8px 8px 72px 8px' },
      nzContentParams: {
        students,
        flagTags: this.flagTags,
        taskAnalysisSrv: this.taskAnalysisSrv,
        questionStatis: this.questionStatis,
        question: this.question,
        currentQuestionNo: this.currentQuestionNo,
        childQuestion: this.childQuestion,
        sharedStudents: this.questionStatis.shared.map(i => {
          return {
            ...i,
            firstName: i.firstName.slice(0, 1) + '**',
          };
        }),
      },
    });
  }

  /**
   * 显示有选择题照片的学生列表详情
   * @since 26.5.19
   */
  viewChoiceDraftsStudents(event) {
    event.stopPropagation();
    event.preventDefault();
    this.drawerSrv.create({
      nzWidth: 800,
      nzTitle: null,
      nzClosable: false,
      nzContent: QuestionStatisStudentComponent,
      nzBodyStyle: { padding: '8px 8px 72px 8px' },
      nzContentParams: {
        question: this.question,
        childQuestion: this.childQuestion,
        questionStatis: this.questionStatis,
        taskAnalysisSrv: this.taskAnalysisSrv,
        currentQuestionNo: this.currentQuestionNo,
        students: this.questionStatis.answers.filter(e => (e.photos ?? []).length),
        sharedStudents: this.questionStatis.shared.map(i => {
          return {
            ...i,
            firstName: i.firstName.slice(0, 1) + '**',
          };
        }),
      },
    });
  }

  /**
   * 变更当前题目的查看类型
   */
  questionTabChange() {
    if (this.questionTabIndex) {
      this.getQuestionGraph();
    }
  }

  // 错误答案tab单击事件
  clickWrongTab(event, answer: WrongAnswerData) {
    const students = this.questionStatis.answers.filter(x => x.answer === answer.answer);
    const params = {
      students: students,
      value: String(answer.answer),
      label: String(answer.answer),
      count: answer.count,
    };
    this.viewStudent(event, params);
  }

  /**
   * 点击举一反三
   * @param {Question} question
   */
  onQuickUptake(question: Question) {
    const questionNo = this.task.questionStatis.find(x => x.questionId === question.id).no;
    this.drawerSrv.create({
      nzTitle: null,
      nzClosable: false,
      nzContent: QuickUptakeComponent,
      nzContentParams: {
        question: {
          ...question,
          no: questionNo,
        },
        statis: this.questionStatis,
        taskAnalysisSrv: this.taskAnalysisSrv,
      },

      nzWidth: '800px',
      nzBodyStyle: {
        padding: 0,
        background: '#f0f0f0',
      },
    });
  }

  /**
   * 点击AI变式
   * @param {Question} question
   */
  onAiVariant(question: Question) {
    this.drawerSrv.create({
      nzTitle: null,
      nzClosable: false,
      nzContent: AiVariantComponent,
      nzContentParams: {
        question,
      },
      nzWidth: '800px',
      nzBodyStyle: {
        padding: 0,
        background: '#f0f0f0',
      },
    });
  }

  // 创建靶向班
  async createTargetClassroom() {
    // const classroomId = this.taskAnalysisSrv.isClassroomTask ? this.task.classroomId : null;
    // const gradeId = (await this.classSrv.getById(classroomId)).gradeId;
    const gradeId = this.task.gradeId;
    const name = this.task.title + '第' + this.currentQuestionNo + '题 靶向班';
    const users = uniqBy(
      this.questionStatisAnswers
        .filter(i => i.checked)
        .map(i => i.students)
        .flat(),
      item => item.userId
    );
    this.taskAnalysisSrv.createTargetClassroom(gradeId, name, users);
  }

  onTarget(graph: TaskAnalysisGraph) {
    const url = this.router.createUrlTree([`/target/for-knowledge/${graph.id}`], {
      queryParams: {
        taskClassId: [1, 2, 3],
        scope: 'all',
        compareWith: 'avgGrade',
        nodeId: graph.id,
        nodeName: graph.name,
        from: dayjs().subtract(6, 'month').format('YYYY-MM-DD'),
        to: dayjs().format('YYYY-MM-DD'),
        classroomId:
          this.taskAnalysisSrv.currentTaskId === 'my'
            ? -2
            : this.taskAnalysisSrv.currentTaskId === 'all'
              ? -3
              : this.task.classroomId,
        gradeId: this.task.gradeId,
        subjectId: this.task.subjectId,
      },
    });
    window.open(url.toString(), '_blank');
  }

  assignNode(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const isStem = this.question.model === QuestionType.Stem;
    const question = isStem ? (this.childQuestion ?? this.question) : this.question;
    this.questionTpl().questionSrv.assignNode(question);
  }

  /**
   * 修改 优选题目数据
   * @param e
   */
  bookletImportChange(e: boolean) {
    /**
     * 创建一个新的 task 对象，以触发变更检测
     * 因为这里需要和图表联动
     */
    this.task = {
      ...this.task,
      questionStatis: this.task.questionStatis.map(i => {
        if (i.questionId === this.question.id) {
          return { ...i, preferred: e };
        }
        return i;
      }),
    };
  }

  async enlargeQuestion() {
    const list = await this.taskAnalysisSrv.unfinishedStudents$;
    const option = {
      taskAnalysis: this.task,
      currentTaskId: this.taskAnalysisSrv.currentTaskId,
      selectedQuestionNo: this.currentQuestionNo,
      taskUnfinishedStudent: list,
    };
    this.questionExplainSrv.show(option);
  }

  async goMark() {
    let questionId = this.question.id;
    if (this.childQuestion) {
      questionId = this.childQuestion.id;
    }
    const { questionIds, appeal } = await getTaskMarkQuestion(this.markUtilsSrv, this.task.id, questionId);
    const params: MarkParamsWithTask = {
      taskId: this.task.id,
      operateType: MarkOperateType.MARK,
      mode: MarkMode.QUESTION,
      enter: MarkEnter.TASK,
      subjectId: this.sharedSrv.user.subject_id,
      taskType: this.task.type,
      initQuestionIds: questionIds,
      appeal,
    };
    toMark(this.router, params, this.task.title);
  }

  /**
   * 切换知识点图谱展开/收起状态
   */
  toggleGraphExpansion() {
    this.isGraphExpanded = !this.isGraphExpanded;
  }

  /**
   * 获取要显示的知识点图谱数据
   */
  get displayedTaskQuestionGraph() {
    if (this.isGraphExpanded || this.taskQuestionGraph.length <= 3) {
      return this.taskQuestionGraph;
    }
    return this.taskQuestionGraph.slice(0, 3);
  }

  protected readonly TaskType = TaskTypeEnum;
  protected readonly AlgoJobStatusEnum = AlgoJobStatusEnum;
}

/**
 * 格式化任务题目统计题号
 * @param {TaskQuestionCorrectPercent[]} questionStatis
 */
export function fmtPaperNo(questionStatis: TaskQuestionCorrectPercent[]): any {
  const _questionStatis = cloneDeep([...questionStatis]);
  const paperIds = uniq(_questionStatis.map(q => q.paperId));
  // if (paperIds.length <= 1) return;
  const paperIdMap = new Map();
  paperIds.forEach((id, idx) => {
    paperIdMap.set(id, idx + 1);
  });
  /**
   * 模块数量大于1  再拼模块 index
   * @since 25/3/13
   */
  _questionStatis.forEach(q => {
    q.no = paperIds.length > 1 ? `${paperIdMap.get(q.paperId)}.${q.no}` : q.no;
  });
  return _questionStatis;
}
